"use client";

/**
 * Range-календарь дат аренды — как на сайтах городов (Солнышко), в синем дизайне
 * autopilotrent. Два месяца рядом: тап «дата подачи» → тап «дата возврата»
 * (выделяется диапазон), под календарём — время подачи и возврата, «Готово»
 * применяет. Минимальный срок — minDays (возврат ≥ подача + minDays).
 */

import { useEffect, useRef, useState } from "react";
import { useLocale } from "@/lib/locale-context";

const MONTHS = ["Январь", "Февраль", "Март", "Апрель", "Май", "Июнь", "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь"];
const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS_EN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS_EN = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const TIMES: string[] = [];
for (let h = 0; h < 24; h++) for (const m of [0, 15, 30, 45]) TIMES.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);

type Cell = { day: number; month: number; year: number };

function iso(y: number, m: number, d: number) { return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`; }
function addDaysISO(s: string, n: number) {
  const d = new Date(s + "T00:00:00");
  d.setDate(d.getDate() + n);
  return iso(d.getFullYear(), d.getMonth(), d.getDate());
}
// "HH:MM" + minutes → "HH:MM"; "" если вышли за пределы суток (тот же день невозможен).
function addMin(t: string, mins: number): string {
  const [h, m] = (t || "12:00").split(":").map((x) => Number(x) || 0);
  const tot = h * 60 + m + mins;
  if (tot >= 24 * 60) return "";
  return `${String(Math.floor(tot / 60)).padStart(2, "0")}:${String(tot % 60).padStart(2, "0")}`;
}
// Минимальный интервал аренды в тот же день — 4 часа.
const SAME_DAY_MIN_MINS = 240;
function buildMonthGrid(year: number, month: number): (Cell | null)[] {
  let start = new Date(year, month, 1).getDay() - 1;
  if (start < 0) start = 6;
  const days = new Date(year, month + 1, 0).getDate();
  const grid: (Cell | null)[] = [];
  for (let i = 0; i < start; i++) grid.push(null);
  for (let d = 1; d <= days; d++) grid.push({ day: d, month, year });
  return grid;
}

/* Селект времени — кнопка + прокручиваемый список (синий). */
function TimeSelect({ value, placeholder, floor, onChange }: {
  value: string; placeholder: string; floor?: string; onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  useEffect(() => {
    if (open && listRef.current) {
      const i = TIMES.indexOf(value);
      if (i >= 0) (listRef.current.children[i] as HTMLElement)?.scrollIntoView({ block: "center" });
    }
  }, [open, value]);
  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={`flex h-[44px] w-full items-center gap-2 rounded-xl border bg-white px-3 ${open ? "border-primary" : "border-[#C9D3E6]"}`}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className="shrink-0"><circle cx="12" cy="12" r="9" stroke="#0039A6" strokeWidth="1.8" /><path d="M12 7v5l3.5 2" stroke="#0039A6" strokeWidth="1.8" strokeLinecap="round" /></svg>
        <span className={`flex-1 text-left text-[15px] ${value ? "text-black" : "text-[#8791AD]"}`}>{value || placeholder}</span>
        <svg width="12" height="8" viewBox="0 0 12 8" className={`shrink-0 transition ${open ? "rotate-180" : ""}`}><path d="M1 1.5 6 6.5l5-5" stroke="#8791AD" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div ref={listRef} className="absolute bottom-[calc(100%+6px)] left-0 z-[90] max-h-[240px] w-full overflow-y-auto rounded-xl bg-white py-1 shadow-[0_8px_30px_rgba(20,29,69,0.18)]">
          {TIMES.map((t) => {
            const dis = !!floor && t < floor;
            return (
              <button key={t} type="button" disabled={dis} onClick={() => { if (!dis) { onChange(t); setOpen(false); } }}
                className={`block w-full px-3 py-1.5 text-center text-[14px] transition ${dis ? "cursor-not-allowed text-[#C3CAD9]" : t === value ? "bg-[#EAF0FB] font-semibold text-primary" : "text-text hover:bg-[#F6FAFF]"}`}>
                {t}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function RangeDateTimePicker({
  startISO, endISO, startTime, endTime, minDate, minTime, minDays = 1,
  onRangeChange, onStartTime, onEndTime, onClose, instant = false,
}: {
  startISO: string; endISO: string; startTime: string; endTime: string;
  minDate?: string; minTime?: string; minDays?: number;
  onRangeChange: (start: string, end: string) => void;
  onStartTime: (v: string) => void; onEndTime: (v: string) => void; onClose: () => void;
  // instant: применять выбор сразу (без кнопки «Готово») — отдаём даты/время
  // родителю на каждый клик. Используется в попапе «Подобрать автомобиль».
  instant?: boolean;
}) {
  const { t, locale } = useLocale();
  const monthNames = locale === "en" ? MONTHS_EN : MONTHS;
  const weekdayNames = locale === "en" ? WEEKDAYS_EN : WEEKDAYS;
  const today = new Date();
  const todayISO = iso(today.getFullYear(), today.getMonth(), today.getDate());
  const base = startISO ? new Date(startISO + "T00:00:00") : today;
  const [view, setView] = useState({ y: base.getFullYear(), m: base.getMonth() });
  const [selStart, setSelStart] = useState(startISO || "");
  const [selEnd, setSelEnd] = useState(endISO || "");
  const [startTouched, setStartTouched] = useState(false);
  const [endTouched, setEndTouched] = useState(false);
  // Время держим в драфте и отдаём родителю ТОЛЬКО по «Готово» (как и даты),
  // чтобы в поле поиска время не появлялось до подтверждения.
  const [draftStart, setDraftStart] = useState(startTime || "");
  const [draftEnd, setDraftEnd] = useState(endTime || "");

  // На мобиле — ОДИН месяц (как на Солнышко), на десктопе — два рядом.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const monthB = view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 };
  const months = isMobile ? [{ y: view.y, m: view.m }] : [{ y: view.y, m: view.m }, monthB];

  const minEndISO = selStart ? addDaysISO(selStart, Math.max(1, minDays)) : "";
  const pickingEnd = !!selStart && !selEnd;
  const hasRange = !!(selStart && selEnd);

  const isPast = (c: Cell) => {
    const s = iso(c.year, c.month, c.day);
    if (s < todayISO) return true;
    if (minDate && s < minDate) return true;
    return false;
  };
  const isDisabled = (c: Cell) => {
    if (isPast(c)) return true;
    if (pickingEnd) { const s = iso(c.year, c.month, c.day); if (s > selStart && s < minEndISO) return true; }
    return false;
  };

  const autoPickup = (s: string) => (s === todayISO ? (minTime || "12:00") : "12:00");
  function startNew(s: string) {
    setSelStart(s); setSelEnd("");
    const ds = startTouched ? draftStart : autoPickup(s);
    if (!startTouched) setDraftStart(ds);
    if (instant) { onStartTime(ds || autoPickup(s)); onRangeChange(s, ""); }
  }
  function pick(c: Cell) {
    const s = iso(c.year, c.month, c.day);
    if (!selStart || (selStart && selEnd)) { startNew(s); return; }
    if (s < selStart) { startNew(s); return; }
    if (s === selStart) {
      // Возврат в тот же день: разрешён при minDays<=1, минимум через 4 часа
      // после подачи. Поздняя подача (после 20:00) — тот же день невозможен.
      const fl = addMin(draftStart || autoPickup(selStart), SAME_DAY_MIN_MINS);
      if (minDays > 1 || !fl) { startNew(s); return; }
      setSelEnd(s);
      const de = endTouched && draftEnd >= fl ? draftEnd : fl;
      setDraftEnd(de);
      if (instant) { onEndTime(de); onRangeChange(selStart, s); }
      return;
    }
    setSelEnd(s);
    const de = endTouched ? draftEnd : "12:00";
    if (!endTouched) setDraftEnd(de);
    if (instant) { onEndTime(de || "12:00"); onRangeChange(selStart, s); }
  }
  // «Готово» — только тут отдаём родителю и даты, и время.
  function commit() {
    if (selStart) {
      const st = draftStart || autoPickup(selStart);
      let et = draftEnd || "12:00";
      let end = selEnd || minEndISO;
      // Возврат в тот же день: время возврата минимум подача+4ч; если подача
      // поздняя и тот же день невозможен — переносим возврат на следующий день.
      if (end === selStart) {
        const fl = addMin(st, SAME_DAY_MIN_MINS);
        if (!fl) end = addDaysISO(selStart, 1);
        else if (et < fl) et = fl;
      }
      onStartTime(st);
      onEndTime(et);
      onRangeChange(selStart, end);
    }
    onClose();
  }

  return (
    <div className={isMobile ? "w-full p-2" : "w-[600px] max-w-[calc(100vw-32px)] p-4"}>
      <div className="mb-1 flex items-center justify-between px-1">
        <button type="button" onClick={() => setView((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))} className="flex size-8 items-center justify-center rounded-lg text-primary transition hover:bg-bg">
          <svg viewBox="0 0 24 24" fill="none" className="size-5"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <button type="button" onClick={() => setView((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))} className="flex size-8 items-center justify-center rounded-lg text-primary transition hover:bg-bg">
          <svg viewBox="0 0 24 24" fill="none" className="size-5"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>

      <div className="flex gap-6">
        {months.map((mo) => (
          <div key={`${mo.y}-${mo.m}`} className="flex-1">
            <div className="mb-2 text-center text-[15px] font-semibold text-text">{monthNames[mo.m]} {mo.y}</div>
            <div className="grid grid-cols-7">
              {weekdayNames.map((w) => <div key={w} className="py-1 text-center text-[12px] font-medium text-text-muted">{w}</div>)}
              {buildMonthGrid(mo.y, mo.m).map((c, i) => {
                if (!c) return <div key={i} className="h-9" />;
                const s = iso(c.year, c.month, c.day);
                const isStart = !!selStart && s === selStart;
                const isEnd = !!selEnd && s === selEnd;
                const inRange = hasRange && s > selStart && s < selEnd;
                const endpoint = isStart || isEnd;
                const dis = isDisabled(c);
                const band = hasRange && (isStart || isEnd || inRange);
                const isTodayCell = s === todayISO;
                return (
                  <div key={i} className={`flex h-9 items-center justify-center ${band ? "bg-[#EAF0FB]" : ""} ${band && isStart ? "rounded-l-full" : ""} ${band && isEnd ? "rounded-r-full" : ""}`}>
                    <button
                      type="button"
                      disabled={dis}
                      onClick={() => !dis && pick(c)}
                      className={`flex size-9 items-center justify-center rounded-full text-[14px] transition ${
                        endpoint ? "bg-primary font-semibold text-white"
                          : dis ? "cursor-not-allowed text-text-muted/40"
                          : `cursor-pointer hover:bg-surface-2 ${isTodayCell ? "font-semibold text-primary" : "text-text"}`
                      }`}
                    >
                      {c.day}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 text-center text-[14px] text-text-muted">
        {pickingEnd ? t("picker.selectReturn") : t("picker.range")}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1.5 text-[13px] text-text-muted">
          {t("picker.pickupTime")}
          <TimeSelect value={draftStart} placeholder={t("search.time")} floor={selStart === todayISO ? minTime : undefined} onChange={(v) => {
            setStartTouched(true); setDraftStart(v);
            if (selEnd && selEnd === selStart) { // тот же день: тянем время возврата за подачей (+4ч)
              const fl = addMin(v, SAME_DAY_MIN_MINS);
              if (!fl) { setSelEnd(addDaysISO(selStart, 1)); if (instant) onRangeChange(selStart, addDaysISO(selStart, 1)); }
              else if ((draftEnd || "12:00") < fl) { setDraftEnd(fl); if (instant) onEndTime(fl); }
            }
            if (instant) onStartTime(v);
          }} />
        </label>
        <label className="flex flex-col gap-1.5 text-[13px] text-text-muted">
          {t("picker.returnTime")}
          <TimeSelect value={draftEnd} placeholder={t("search.time")} floor={selEnd && selEnd === selStart ? (addMin(draftStart || autoPickup(selStart), SAME_DAY_MIN_MINS) || undefined) : undefined} onChange={(v) => { setEndTouched(true); setDraftEnd(v); if (instant) onEndTime(v); }} />
        </label>
      </div>

      {!instant && (
        <button type="button" onClick={commit} className="mt-4 w-full cursor-pointer rounded-xl bg-primary py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover">
          {t("search.done")}
        </button>
      )}
    </div>
  );
}
